/**
 * WorkflowRevisionContext — Revision target slice.
 *
 * Owns `revisionAgent` and `revisionStep` — the metadata that says
 * "we're currently revising agent X at step Y." Per audit §1.5,
 * `isRevising` lives in WorkflowMachineContext (the residue) because
 * it's a UI flag consumed by 9 step components, while these two
 * fields are write-only metadata consumed only by ReviewPanel's
 * `setRevisionState` calls.
 *
 * The `setRevisionState(isRevising, agent, step)` call sites
 * (3 in ReviewPanel) are preserved by an orchestrator action-creator
 * in the Machine residue that dispatches `isRevising` locally and
 * calls `workflowRevision.setRevisionTarget(agent, step)` for the
 * other two — see Machine residue for the orchestrator.
 *
 * Refs:
 * - Modularity Epic 4 plan §6 (Story 4.6 wrap-up)
 * - docs/ATHENA_WORKFLOW_CONTEXT_SLICES.md §3.1 (isRevising deviation)
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';

const WorkflowRevisionContext = createContext(null);

export const REVISION_ACTIONS = {
  SET_REVISION_TARGET: 'SET_REVISION_TARGET',
};

export const initialRevisionState = {
  revisionAgent: null,
  revisionStep: null,
};

export function revisionReducer(state, action) {
  switch (action.type) {
    case REVISION_ACTIONS.SET_REVISION_TARGET:
      return {
        ...state,
        revisionAgent: action.payload.revisionAgent,
        revisionStep: action.payload.revisionStep,
      };

    default:
      return state;
  }
}

export function WorkflowRevisionProvider({ children }) {
  const [state, dispatch] = useReducer(revisionReducer, initialRevisionState);

  const setRevisionTarget = useCallback((revisionAgent, revisionStep) => {
    dispatch({
      type: REVISION_ACTIONS.SET_REVISION_TARGET,
      payload: { revisionAgent, revisionStep },
    });
  }, []);

  const contextValue = useMemo(() => ({
    ...state,
    setRevisionTarget,
  }), [state, setRevisionTarget]);

  return (
    <WorkflowRevisionContext.Provider value={contextValue}>
      {children}
    </WorkflowRevisionContext.Provider>
  );
}

export function useWorkflowRevision() {
  const context = useContext(WorkflowRevisionContext);
  if (!context) {
    throw new Error('useWorkflowRevision must be used within a WorkflowRevisionProvider');
  }
  return context;
}
